<template>
  <div>
    <breadcrumb-block>
      <breadcrumb-router-link to="/orgapplications">
        {{ $t("commonStrings.toOverview") }}
      </breadcrumb-router-link>
    </breadcrumb-block>
    <vee-form ref="generalObserver">
      <v-row>
        <v-col
          cols="12"
          lg="6"
          order-lg="2"
        >
          <person-information
            :initializing="initializing"
            :init-application-id="$route.params.id"
            :title="$t('personInformationStrings.title')"
          />
        </v-col>
        <v-col
          cols="12"
          lg="6"
          order-lg="1"
        >
          <general-details-panel
            :application="application"
          />
        </v-col>
      </v-row>
    </vee-form>
    <v-row>
      <v-col>
        <v-expansion-panels
          multiple
          :model-value="[0, 1, 2, 3]"
        >
          <salary-panel
            :paying-orgs="application.orgs"
            :claims="application.claims.salaries"
            :immutable-salary-claims-collection="immutableSalaryClaimsCollection"
            :editable-salary-claim-paying-orgs="editableSalaryClaimPayingOrgs"
            :enable-add-new="generalInfoIsValid"
          />
          <trips-panel
            :paying-orgs="application.orgs"
            :claims="application.claims.trips"
            :can-use-salary-fraction="canUseSalaryFraction"
            :enable-add-new="generalInfoIsValid"
          />
          <vee-form
            ref="dietObserver"
            slim
          >
            <diet-panel
              :diet="application.claims.diet"
              :can-use-salary-fraction="canUseSalaryFraction"
              :enable-add-new="generalInfoIsValid"
              @add-default-diet="onDietAdd"
              @remove-current-diet="onDietRemove"
            />
          </vee-form>
          <receipts-panel
            :paying-orgs="application.orgs"
            :claims="application.claims.receipts"
            :can-use-salary-fraction="canUseSalaryFraction"
            :enable-add-new="generalInfoIsValid"
          />
        </v-expansion-panels>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <summary-panel :claims="application.claims" />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-btn
          rounded
          class="primary-inverted-button"
          :loading="isSaving"
          :disabled="!hasChanges || isSaving || isTypingResponse"
          :block="xs"
          @click="saveClicked"
        >
          {{ $t("editApplicationStrings.saveBtn") }}
        </v-btn>
        <v-btn
          rounded
          :loading="isSaving"
          :disabled="!canSend || isSaving || isTypingResponse"
          :block="xs"
          class="primary-button ml-0 ml-sm-2 mt-2 mt-sm-0"
          @click="sendClicked"
        >
          {{ $t("editApplicationStrings.sendBtn") }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row
      v-if="
        (application.logEvents && application.logEvents.length > 0) ||
          (application.comments && application.comments.length > 0)
      "
    >
      <v-col>
        <communication-panel
          :log="application.logEvents"
          :comments="application.comments"
          :orgs="payingOrgs"
          :application-person-id="application.personId"
          :current-user="user"
          :current-org-id="currentOrgId"
          :application-id="application.id"
          :can-write-as-org="false"
          :is-draft="application.isDraft"
          :is-typing-response="isTypingResponse"
          :initializing="initializing"
          :source="application.databaseSource"
          @reply="appendReply"
          @comment-changed="setIsTypingResponse($event)"
        />
      </v-col>
    </v-row>

    <div class="mt-4" />
  </div>
</template>

<script>
import _ from "lodash";
import { api, urls } from "@/utils/api";

import { Form as VeeForm } from "vee-validate";
import ObjectId from "bson-objectid";
import { compare } from "fast-json-patch";
import { mapActions, mapMutations, mapGetters } from "vuex";
import { handleError } from "@/utils/errorHandler";

import OrgApplicationStatusTypes from "@/enums/OrgApplicationStatusTypes";
import PersonInformation from "@/views/applications/components/PersonInformation";
import SalaryPanel from "./salaries/SalaryPanel";
import ReceiptsPanel from "./receipts/ReceiptsPanel";
import TripsPanel from "./trips/TripsPanel";
import DietPanel from "./diet/DietPanel";
import SummaryPanel from "@/views/applications/components/summary/SummaryPanel";
import GeneralDetailsPanel from "./components/GeneralDetailsPanel";
import CommunicationPanel from "@/views/applications/components/communication/CommunicationPanel";

import validationErrors from "@/mixins/validationErrors";
import application from "@/mixins/application";
import BreadcrumbBlock from "@/views/common/components/BreadcrumbBlock";
import BreadcrumbRouterLink from "@/views/common/components/BreadcrumbRouterLink";
import { useDisplay } from 'vuetify';
import { useSnacks } from '@/composables/snacks';
import { useOidc } from '@/composables/oidc';

export default {
  name: "EditApplication",
  components: {
    VeeForm,
    PersonInformation,
    ReceiptsPanel,
    SalaryPanel,
    SummaryPanel,
    TripsPanel,
    DietPanel,
    GeneralDetailsPanel,
    CommunicationPanel,
    BreadcrumbBlock,
    BreadcrumbRouterLink
  },
  mixins: [application, validationErrors],
  setup() {
    const { xs } = useDisplay();
    const { addSnackbar } = useSnacks();
    const { user } = useOidc();
    return { xs, addSnackbar, user };
  },
  data: function () {
    return {
      generalInfoIsValid: false
    };
  },
  methods: {
    ...mapActions({
      fetchRateConfigurationsForOrg:
        "application/rateConfigurationsStore/fetchByOrgId",
      fetchTaxRates: "currentTaxRatePeriodStore/fetch"
    }),
    ...mapMutations({
      setPayingOrgs: "application/payingOrgsStore/setPayingOrgs"
    }),
    ...mapGetters({
      getRateConfiguration:
        "application/rateConfigurationsStore/rateConfiguration"
    }),
    onDietAdd() {
      this.application.claims.diet = { stays: [] };
    },
    onDietRemove() {
      delete this.application.claims["diet"];
    },
    // we need to clean rejections if case of
    // application has been rejected and returned
    // to user to do corrections
    async saveClicked() {
      if (!this.application.isDraft) {
        this.application.isDraft = true;
      }
      this.application.rateConfigurations = null;
      this.resetOrgActions();

      await this.save();
    },
    getUsedRateConfigurations() {
      const getRateConfigurationFunc = this.getRateConfiguration();

      const rateConfigurationIds = this.getRateConfigurationsUsedInNonDietClaims(getRateConfigurationFunc);
      const dietRateConfigurationIds = this.getRateConfigurationsUsedInDietClaims(getRateConfigurationFunc);

      return rateConfigurationIds.concat(dietRateConfigurationIds.filter(x => rateConfigurationIds.indexOf(x) < 0));
    },
    getRateConfigurationsUsedInNonDietClaims(getRateConfigurationFunc) {
      const rateConfigurationIds = [];

      Object.values(this.application.claims)
        .flat()
        .filter(x => x?.date && x?.payingOrgs && !x?.isDeleted)
        .forEach(function (claim) {
          for (const orgId of claim.payingOrgs) {
            const rateConfiguration = getRateConfigurationFunc(
              orgId,
              claim.date
            );

            if (
              rateConfiguration &&
              !rateConfigurationIds.includes(rateConfiguration.id)
            ) {
              rateConfigurationIds.push(rateConfiguration.id);
            }
          }
        }, this);

        return rateConfigurationIds;
    },
    getRateConfigurationsUsedInDietClaims(getRateConfigurationFunc) {
      if (!this.application.claims.diet?.stays ||
        !this.application.claims.diet.payingOrgs) {
          return [];
      }

      const rateConfigurationIds = [];

        for (const stay of this.application.claims.diet.stays) {
          for (const orgId of this.application.claims.diet.payingOrgs) {
            const rateConfiguration = getRateConfigurationFunc(
              orgId,
              stay.date
            );

            if (
              rateConfiguration &&
              !rateConfigurationIds.includes(rateConfiguration.id)
            ) {
              rateConfigurationIds.push(rateConfiguration.id);
            }
          }
        }

      return rateConfigurationIds;
    },
    async sendClicked() {
      this.resetOrgActions();
      this.application.rateConfigurations = this.getUsedRateConfigurations();

      await this.save();
    },
    resetOrgActions() {
      for (const orgId in this.application.orgs) {
        if (this.application.orgs[orgId].rejection) {
          this.application.orgs[orgId].rejection = null;
          this.application.orgs[orgId].orgApplicationStatus = OrgApplicationStatusTypes.Pending;
        }
      }
    },
    async save() {
      const observers = [this.$refs.generalObserver, this.$refs.dietObserver];

      const validationResults = await Promise.all(
        observers.map(async x => await x.validate())
      );
      const isDataValid = validationResults.every(x => x);

      if (!isDataValid) {
        this.showValidationErrorSnackbar(observers);
        return;
      }

      if (
        this.application.claims?.diet?.stays?.length &&
        !this.application.claims?.diet?.amount
      ) {
        this.addSnackbar(this.$t("editApplicationStrings.dietWithoutAmount"),
          "error"
        );

        return;
      }

      if (!this.claimCount) {
        this.addSnackbar(this.$t("editApplicationStrings.noClaimsError"),
          "error"
        );
        return;
      }

      if (this.application.rateConfigurations && this.claimTotalAmount <= 0) {
        this.addSnackbar(this.$t("editApplicationStrings.noClaimAmountError"),
          "error"
        );
        return;
      }

      this.application.orgs = _.pick(this.application.orgs, this.usedOrgs);

      const filteredPayingOrgs = this.payingOrgs.filter(
        x => this.application.orgs[x.id]
      );

      this.setPayingOrgs(filteredPayingOrgs);

      if (ObjectId.isValid(this.application.id)) {
        this.updateApplication("/orgapplications");
      } else {
        this.insertApplication();
      }
    },
    async insertApplication() {
      try {
        const applicationToInsert = this.getApplicationWithDraftState(
          this.application
        );
        const response = await api.v1.applications.post(applicationToInsert);

        if (response.status === 201) {
          this.addSnackbar(this.$t("editApplicationStrings.saveAlerts.saved"),
            "success",
            true
          );
          this.$router.push("/orgapplications");
        }
      } catch (error) {
        await handleError(error);
      }
    },
    async validateGeneral() {
      const observer = this.$refs.generalObserver;

      const valid = await observer.validate();
      this.generalInfoIsValid = valid;
    }
  },
  computed: {
    hasChanges() {
      return compare(this.originalState, this.application).length;
    },
    canSend() {
      // if it's just created can be send directly
      // if it's existing (valid object id) and in draft can be send
      return (
        this.hasChanges ||
        (ObjectId.isValid(this.application.id) && this.application.isDraft)
      );
    },
    ...mapGetters({
      payingOrgs: "application/payingOrgsStore/payingOrgs",
      currentOrgId: "contextStore/orgId"
    }),
    isPosting() {
      return this.urlState(urls.v1.applications.post());
    },
    isPatching() {
      if (this.application?.id) {
        return this.urlState(urls.v1.applications.patch(this.application.id));
      }

      return false;
    },
    isSaving() {
      return this.isPosting || this.isPatching;
    },
    usedOrgs() {
      return Object.values(this.application.claims)
        .flat()
        .filter(x => x?.payingOrgs && !x.isDeleted)
        .map(x => x.payingOrgs)
        .flat()
        .map(x => x.toString());
    },
    claimCount() {
      return Object.values(this.application.claims)
        .flat()
        .filter(x => x && !x.isDeleted).length;
    },
    claimTotalAmount() {
      return _.sum(
        Object.values(this.application.claims)
          .flat()
          .filter(x => x && !x.isDeleted)
          .map(x => x.amount));
    }

  },
  watch: {
    "application.applicationName": {
      async handler() {
        await this.validateGeneral();
      }
    },
    "application.accountNumber": {
      async handler() {
        await this.validateGeneral();
      }
    },
    async payingOrgs(payingOrgs) {
      // keys have to be strings for valid json
      const payingOrgIds = payingOrgs.map(x => x.id.toString());

      for (const payingOrgId of payingOrgIds) {
        if (!this.application.orgs[payingOrgId]) {
          this.application.orgs[payingOrgId] = { orgApplicationStatus: OrgApplicationStatusTypes.Pending };
        }
      }

      for (const orgId in this.application.orgs) {
        if (!payingOrgIds.includes(orgId)) {
          delete this.application.orgs[orgId];
        }
      }

      if (payingOrgs?.length) {
        const orgIds = payingOrgs.map(x => x.id);

        for (const orgId of orgIds) {
          try {
            await this.fetchRateConfigurationsForOrg(orgId);
          } catch (error) {
            await handleError(error);
          }
        }
      }

      await this.validateGeneral();
    }
  },
  async mounted() {
    if (location.href.indexOf("orgapplications/create") < 0) {
      await this.fetch();
    } else {
      this.application.functionalityId = Number(this.user.application_id);
      this.application.personId = Number(this.user.personid);
    }

    this.initializing = false;
  },
  async beforeMount() {
    try {
      await this.fetchTaxRates();
    } catch (error) {
      await handleError(error);
    }
  }
};
</script>
